import React, { useState, useCallback, useEffect } from 'react';
import { ReportFilters } from '../../types/Report';
import {
  comparisonDashboardService,
  ComparisonWidget,
  GroupByDimension,
} from '../../services/comparisonDashboardService';
import { reportFiltersToComparisonFilters } from '../../utils/reportFiltersComparison';
import ComparisonBreakdownGrid from './ComparisonBreakdownGrid';

export interface ExecutiveComparisonBreakdownPanelProps {
  reportFilters: ReportFilters;
  /** When false, show a prompt instead of fetching (matches executive “run report” flow). */
  enabled: boolean;
}

/**
 * Comparison-style grouped metrics for the executive dashboard (same API as Comparison Dashboard).
 */
const ExecutiveComparisonBreakdownPanel: React.FC<ExecutiveComparisonBreakdownPanelProps> = ({
  reportFilters,
  enabled,
}) => {
  const [groupBy, setGroupBy] = useState<GroupByDimension>('product');
  const [widgets, setWidgets] = useState<ComparisonWidget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (f: ReportFilters, gb: GroupByDimension) => {
    const comparisonFilters = reportFiltersToComparisonFilters(f);
    if (!comparisonFilters.start_date || !comparisonFilters.end_date) {
      setWidgets([]);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const data = await comparisonDashboardService.getComparisonData(comparisonFilters, gb);
      setWidgets(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load breakdown';
      setError(message);
      setWidgets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setWidgets([]);
      return;
    }
    loadData(reportFilters, groupBy);
  }, [enabled, groupBy, reportFilters, loadData]);

  if (!enabled) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '2.5rem 1rem',
          color: '#6b7280',
          fontSize: '0.95rem',
        }}
      >
        Run the report to load the dimensional breakdown.
      </div>
    );
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {error && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            borderRadius: '6px',
            border: '1px solid #fecaca',
            fontSize: '0.9rem',
          }}
        >
          {error}
        </div>
      )}
      <ComparisonBreakdownGrid
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        widgets={widgets}
        loading={loading}
      />
    </div>
  );
};

export default ExecutiveComparisonBreakdownPanel;
